import React, { useState, useEffect } from 'react';
import Map from './map.js';
import Healers from './healers.js';
import './search.css';
import axios from 'axios';
import Dropdown from '../../dropdown/Dropdown';

// Enhanced CSS reload for search bar styles
const forceCSSReload = () => {
    const links = document.querySelectorAll('link[rel="stylesheet"]');
    links.forEach(link => {
        if (link.href.includes('search.css')) {
            link.href = link.href + '?v=' + Date.now();
        }
    });

    // Force re-render of search bar elements
    const searchElements = document.querySelectorAll('.search-container, .search-bar-container, .search-input-group');
    searchElements.forEach(element => {
        if (element) {
            element.style.display = 'none';
            setTimeout(() => {
                element.style.display = '';
            }, 10);
        }
    });

    // Force style recalculation
    setTimeout(() => {
        const searchContainer = document.querySelector('.search-container');
        if (searchContainer) {
            searchContainer.style.opacity = '0.99';
            setTimeout(() => {
                searchContainer.style.opacity = '1';
            }, 50);
        }
    }, 100);
};

// Persistent filter state
let city = null;
let service = null;
let deliveryFormat = '0';

const Search = () => {
    const victoria = { view: { lat: 48.407326, lng: -123.329773 } };

    const [initHealers, setInitHealers] = useState(null);
    const [markers, setMarkers] = useState(null);
    const [currMarkers, setCurrMarkers] = useState(null);
    const [healers, setHealers] = useState(null);
    const [active, setActive] = useState(null);
    const [range, setRange] = useState(50);
    const [location, setLocation] = useState(victoria);
    const [searchTerm, setSearchTerm] = useState("");
    const [view, setView] = useState("healers"); // 'healers' or 'map'
    const [selectedHealerId, setSelectedHealerId] = useState(null);
    const [isViewTransitioning, setIsViewTransitioning] = useState(false);
    const [mapKey, setMapKey] = useState(0); // Force remount of map component
    const [searchBarKey, setSearchBarKey] = useState(0); // Force re-render of search bar
    const [isMapCleaning, setIsMapCleaning] = useState(false); // Track map cleanup state

    // New filter states
    const [availableServices, setAvailableServices] = useState([]);
    const [availableCities, setAvailableCities] = useState([]);
    const [selectedService, setSelectedService] = useState("");
    const [selectedLocation, setSelectedLocation] = useState("");
    const [selectedDate, setSelectedDate] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [mapsLoaded, setMapsLoaded] = useState(false);
    const [lastApiCall, setLastApiCall] = useState(0);
    const [rateLimited, setRateLimited] = useState(false);
    const [rateLimitRetryTime, setRateLimitRetryTime] = useState(0);

    const handleActiveMarker = (marker) => {
        if (marker === active) return;
        setActive(marker);
    };

    // Simple distance calculation using Haversine formula as fallback
    const calculateDistance = (lat1, lng1, lat2, lng2) => {
        const R = 6371; // Earth's radius in kilometers
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    const handleMapsLoaded = () => {
        setMapsLoaded(true);
        // Recalculate distances when maps are loaded
        if (healers && healers.length > 0) {
            getHealersWithFilter({ range: range });
        }
    };

    const getHealersWithFilter = async (filters = {}) => {
        // Check if we're currently rate limited
        if (rateLimited) {
            const now = Date.now();
            if (now < rateLimitRetryTime) {
                console.log('Still rate limited, waiting...');
                return;
            } else {
                setRateLimited(false);
                setRateLimitRetryTime(0);
            }
        }

        // Throttle API calls to prevent rate limiting
        const now = Date.now();
        if (now - lastApiCall < 2000) { // Increased to 2 seconds between calls
            console.log('API call throttled to prevent rate limiting');
            return;
        }
        setLastApiCall(now);

        if (filters.Cities) city = filters.param;
        if (filters.Services) service = filters.param;
        if (filters.deliveryFormat !== undefined) deliveryFormat = filters.deliveryFormat;

        const rangeToUse = filters.range || range;

        // Send only the parameters that the backend expects
        const apiFilters = {};

        // Only include cityParam if it has a value
        if (city && city.trim() !== '') {
            apiFilters.cityParam = city;
        }

        // Only include serviceParam if it has a value
        if (service && service.trim() !== '') {
            apiFilters.serviceParam = service;
        }

        console.log('Sending API request with filters:', apiFilters);

        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/users/healers`, apiFilters)
                .then((response) => {
                    console.log('API response received:', response.data);

                    // Remove duplicates by ID first
                    const uniqueHealers = response.data.filter((healer, index, self) =>
                        index === self.findIndex(h => h.id === healer.id)
                    );

                    // Check if Google Maps API is available
                    if (mapsLoaded && window.google && window.google.maps && window.google.maps.geometry) {
                        const inRangeResponse = uniqueHealers.filter((healer) => {
                            const currMarkerPos = markers?.find(m => m.id === healer.id);
                            if (currMarkerPos) {
                                try {
                                    const dist = window.google.maps.geometry.spherical.computeDistanceBetween(
                                        new window.google.maps.LatLng(location.view.lat, location.view.lng),
                                        new window.google.maps.LatLng(currMarkerPos.position.lat, currMarkerPos.position.lng)
                                    ) / 1000;

                                    return dist <= rangeToUse;
                                } catch (error) {
                                    console.warn('Error calculating distance for healer:', healer.id, error);
                                    return true; // Include healer if distance calculation fails
                                }
                            }
                            // Include healers without location data (they will be shown in list view but not on map)
                            return true;
                        });

                        setHealers(inRangeResponse);
                        getMarkers(inRangeResponse, false);
                    } else {
                        // Use fallback distance calculation if Google Maps API is not available
                        const inRangeResponse = uniqueHealers.filter((healer) => {
                            const currMarkerPos = markers?.find(m => m.id === healer.id);
                            if (currMarkerPos) {
                                try {
                                    const dist = calculateDistance(
                                        location.view.lat,
                                        location.view.lng,
                                        currMarkerPos.position.lat,
                                        currMarkerPos.position.lng
                                    );
                                    return dist <= rangeToUse;
                                } catch (error) {
                                    console.warn('Error calculating distance for healer:', healer.id, error);
                                    return true; // Include healer if distance calculation fails
                                }
                            }
                            // Include healers without location data (they will be shown in list view but not on map)
                            return true;
                        });

                        if (!mapsLoaded) {
                            console.log('Google Maps API not yet loaded, using fallback distance calculation');
                        } else {
                            console.log('Google Maps API not available, using fallback distance calculation');
                        }
                        setHealers(inRangeResponse);
                        getMarkers(inRangeResponse, false);
                    }
                })
                .catch((error) => {
                    console.error('Error fetching healers:', error);
                    console.error('Error details:', {
                        status: error.response?.status,
                        statusText: error.response?.statusText,
                        data: error.response?.data,
                        message: error.message
                    });

                    // Handle rate limiting specifically
                    if (error.response?.status === 429 ||
                        (error.response?.data && error.response.data.error &&
                            error.response.data.error.includes('Too many requests'))) {
                        console.warn('Rate limit detected, setting cooldown period');
                        setRateLimited(true);
                        setRateLimitRetryTime(Date.now() + 30000); // 30 second cooldown
                        // Keep existing healers instead of clearing them
                        return;
                    }

                    // Set empty healers array to prevent further errors
                    setHealers([]);
                    getMarkers([], false);
                });
        } catch (error) {
            console.error('Error in getHealersWithFilter:', error);
        }
    };

    const getMarkers = async (returnedHealers, first) => {
        const ids = returnedHealers.map(healer => healer.id);

        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/locations`, ids)
            .then((markerResponse) => {
                const responseMarkers = markerResponse.data.map((mrkr) => {
                    const currHealer = returnedHealers.find(h => h.id === mrkr.user_id);
                    return {
                        id: mrkr.user_id,
                        position: {
                            lat: mrkr.lat,
                            lng: mrkr.lng
                        },
                        healer: `${currHealer.firstname} ${currHealer.lastname}`,
                        description: currHealer.description,
                        services: currHealer.services
                    };
                });

                if (first) {
                    setMarkers(responseMarkers);
                    setCurrMarkers(responseMarkers);
                } else {
                    setCurrMarkers(responseMarkers);
                }

                return responseMarkers;
            });
    };

    // Check for healer parameter in URL
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const healerId = urlParams.get('healer');
        if (healerId) {
            setSelectedHealerId(parseInt(healerId));
        }
    }, []);

    // Handle selectedHealerId from map view - switch to healers view and open booking
    useEffect(() => {
        if (selectedHealerId && view === 'map') {
            setView('healers');
        }
    }, [selectedHealerId, view]);

    // CSS reload when component mounts and when view changes
    useEffect(() => {
        forceCSSReload();
    }, [view]); // Re-run when view changes

    // Fetch available services and cities
    useEffect(() => {
        const fetchServices = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/services`);
                setAvailableServices(response.data);
            } catch (error) {
                console.error('Error fetching services:', error);
            }
        };

        const fetchCities = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/locations/cities`);
                setAvailableCities(response.data);
            } catch (error) {
                console.error('Error fetching cities:', error);
            }
        };

        fetchServices();
        fetchCities();
    }, []);

    // Initial load of all healers and markers
    useEffect(() => {
        const getInitialHealers = async () => {
            try {
                // Add delay before initial load to prevent overwhelming the server
                await new Promise(resolve => setTimeout(resolve, 1000));

                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users/healers`);
                // Remove duplicates by ID
                const uniqueHealers = response.data.filter((healer, index, self) =>
                    index === self.findIndex(h => h.id === healer.id)
                );
                setInitHealers(uniqueHealers);
                setHealers(uniqueHealers);
                getMarkers(uniqueHealers, true);
            } catch (error) {
                console.error('Error fetching initial healers:', error);

                // Handle rate limiting specifically
                if (error.response?.status === 429 ||
                    (error.response?.data && error.response.data.error &&
                        error.response.data.error.includes('Too many requests'))) {
                    console.warn('Rate limit detected during initial load');
                    setRateLimited(true);
                    setRateLimitRetryTime(Date.now() + 60000); // 1 minute cooldown
                }

                setInitHealers([]);
                setHealers([]);
            }
        };

        getInitialHealers();
    }, []);

    // NEW: Watch for range change and refilter
    useEffect(() => {
        if (!initHealers || !markers) return;
        getHealersWithFilter({ range });
    }, [range]);

    // Apply filters
    const applyFilters = async () => {
        // Check if we're currently rate limited
        if (rateLimited) {
            const now = Date.now();
            if (now < rateLimitRetryTime) {
                console.log('Still rate limited, cannot apply filters');
                return;
            } else {
                setRateLimited(false);
                setRateLimitRetryTime(0);
            }
        }

        // Throttle API calls to prevent rate limiting
        const now = Date.now();
        if (now - lastApiCall < 2000) { // Increased to 2 seconds between calls
            console.log('Filter API call throttled to prevent rate limiting');
            return;
        }
        setLastApiCall(now);

        setIsLoading(true);
        try {
            const filters = {
                serviceParam: selectedService,
                cityParam: selectedLocation
            };

            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/users/healers`, filters);
            // Remove duplicates by ID
            const uniqueHealers = response.data.filter((healer, index, self) =>
                index === self.findIndex(h => h.id === healer.id)
            );
            setHealers(uniqueHealers);
            getMarkers(uniqueHealers, false);
        } catch (error) {
            console.error('Error applying filters:', error);

            // Handle rate limiting specifically
            if (error.response?.status === 429 ||
                (error.response?.data && error.response.data.error &&
                    error.response.data.error.includes('Too many requests'))) {
                console.warn('Rate limit detected during filter application');
                setRateLimited(true);
                setRateLimitRetryTime(Date.now() + 30000); // 30 second cooldown
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Clear all filters
    const clearFilters = async () => {
        // Check if we're currently rate limited
        if (rateLimited) {
            const now = Date.now();
            if (now < rateLimitRetryTime) {
                console.log('Still rate limited, cannot clear filters');
                return;
            } else {
                setRateLimited(false);
                setRateLimitRetryTime(0);
            }
        }

        // Throttle API calls to prevent rate limiting
        const now = Date.now();
        if (now - lastApiCall < 2000) { // Increased to 2 seconds between calls
            console.log('Clear filters API call throttled to prevent rate limiting');
            return;
        }
        setLastApiCall(now);

        setSelectedService("");
        setSelectedLocation("");
        setSelectedDate("");
        setSearchTerm("");
        setShowFilters(false);

        // Reset to initial healers
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users/healers`);
            // Remove duplicates by ID
            const uniqueHealers = response.data.filter((healer, index, self) =>
                index === self.findIndex(h => h.id === healer.id)
            );
            setHealers(uniqueHealers);
            getMarkers(uniqueHealers, false);
        } catch (error) {
            console.error('Error clearing filters:', error);

            // Handle rate limiting specifically
            if (error.response?.status === 429 ||
                (error.response?.data && error.response.data.error &&
                    error.response.data.error.includes('Too many requests'))) {
                console.warn('Rate limit detected during filter clearing');
                setRateLimited(true);
                setRateLimitRetryTime(Date.now() + 30000); // 30 second cooldown
            }
        }
    };

    // Filter healers by search term and other filters
    const filteredHealers = healers ? healers
        .filter((healer, index, self) =>
            index === self.findIndex(h => h.id === healer.id)
        ) // Remove duplicates by ID first
        .filter(h => {
            const fullName = `${h.firstname} ${h.lastname}`.toLowerCase();
            const services = h.services ? h.services.toLowerCase() : "";
            const description = h.description ? h.description.toLowerCase() : "";
            const cities = h.cities ? h.cities.toLowerCase() : "";

            const matchesSearch = searchTerm === "" ||
                fullName.includes(searchTerm.toLowerCase()) ||
                services.includes(searchTerm.toLowerCase()) ||
                description.includes(searchTerm.toLowerCase());

            const matchesService = selectedService === "" ||
                (h.services && h.services.toLowerCase().includes(selectedService.toLowerCase()));

            const matchesLocation = selectedLocation === "" ||
                (h.cities && h.cities.toLowerCase().includes(selectedLocation.toLowerCase()));

            return matchesSearch && matchesService && matchesLocation;
        }) : [];

    return (
        <div>
            {/* Elegant Search Bar Section */}
            <div className="search-container" key={searchBarKey}>

                {/* Main Search Bar */}
                <div className="search-bar-container">
                    <div className="search-input-group">
                        <input
                            type="text"
                            placeholder="Search healers by name, service, or specialty..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        <div className="view-and-filter-row">
                            <div className="view-toggle-icons">
                                <button
                                    onClick={() => {
                                        // First, completely disable map to prevent conflicts
                                        setIsViewTransitioning(true);
                                        setIsMapCleaning(true);

                                        // Clear any active map markers and state
                                        setActive(null);

                                        // Switch view first
                                        setView('healers');

                                        // Clear search term when switching to healers view to show all healers
                                        setSearchTerm("");

                                        // Reset to initial healers to clear any map-based filtering
                                        if (initHealers) {
                                            setHealers(initHealers);
                                            getMarkers(initHealers, false);
                                        }

                                        // Clear any active filters
                                        setSelectedService("");
                                        setSelectedLocation("");
                                        setSelectedDate("");
                                        setRange(50); // Reset distance filter

                                        // Force search bar re-initialization
                                        setSearchBarKey(prev => prev + 1);

                                        // Longer delay to ensure map cleanup
                                        setTimeout(() => {
                                            forceCSSReload();
                                            // Force DOM update
                                            const searchContainer = document.querySelector('.search-container');
                                            if (searchContainer) {
                                                searchContainer.style.display = 'none';
                                                setTimeout(() => {
                                                    searchContainer.style.display = '';
                                                    setSearchBarKey(prev => prev + 1);
                                                    setIsViewTransitioning(false);
                                                }, 100);
                                            } else {
                                                setSearchBarKey(prev => prev + 1);
                                                setIsViewTransitioning(false);
                                            }
                                            setIsMapCleaning(false);
                                        }, 800); // Much longer delay for map cleanup
                                    }}
                                    className={`view-toggle-icon ${view === 'healers' ? 'active' : ''}`}
                                    title="Browse Healers"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="3" width="7" height="7"></rect>
                                        <rect x="14" y="3" width="7" height="7"></rect>
                                        <rect x="14" y="14" width="7" height="7"></rect>
                                        <rect x="3" y="14" width="7" height="7"></rect>
                                    </svg>
                                </button>
                                <button
                                    onClick={() => {
                                        setIsViewTransitioning(true);
                                        setView('map');
                                        // Force remount of map component
                                        setMapKey(prev => prev + 1);
                                        setTimeout(() => {
                                            setIsViewTransitioning(false);
                                        }, 500); // Increased delay
                                    }}
                                    className={`view-toggle-icon ${view === 'map' ? 'active' : ''}`}
                                    title="Map View"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polygon points="1,6 1,22 8,18 16,22 23,18 23,2 16,6 8,2"></polygon>
                                    </svg>
                                </button>
                            </div>
                            <button
                                className="filter-toggle-btn"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"></polygon>
                                </svg>
                                Filters
                            </button>
                        </div>
                    </div>

                    {/* Advanced Filters */}
                    {showFilters && (
                        <div className="filters-panel">
                            <div className="filters-grid">
                                <div className="filter-group">
                                    <label className="filter-label">Service Type</label>
                                    <select
                                        value={selectedService}
                                        onChange={(e) => setSelectedService(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="">All Services</option>
                                        {availableServices.map(service => (
                                            <option key={service.id} value={service.name}>
                                                {service.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="filter-group">
                                    <label className="filter-label">Location</label>
                                    <select
                                        value={selectedLocation}
                                        onChange={(e) => setSelectedLocation(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="">All Locations</option>
                                        {availableCities.map(city => (
                                            <option key={city} value={city}>
                                                {city}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="filter-group">
                                    <label className="filter-label">Available Date</label>
                                    <input
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        className="filter-date"
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </div>

                                <div className="filter-group">
                                    <label className="filter-label">Distance (km)</label>
                                    <input
                                        type="range"
                                        min="5"
                                        max="100"
                                        value={range}
                                        onChange={(e) => setRange(e.target.value)}
                                        className="filter-range"
                                    />
                                    <span className="range-value">{range} km</span>
                                </div>
                            </div>

                            <div className="filter-actions">
                                <button onClick={applyFilters} className="apply-filters-btn">
                                    {isLoading ? (
                                        <div className="spinner"></div>
                                    ) : (
                                        'Apply Filters'
                                    )}
                                </button>
                                <button onClick={clearFilters} className="clear-filters-btn">
                                    Clear All
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Rate limit warning */}
            {rateLimited && (
                <div style={{
                    background: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    color: '#856404',
                    padding: '10px',
                    margin: '10px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontSize: '14px'
                }}>
                    ⚠️ Rate limit reached. Please wait a moment before making more requests.
                    {rateLimitRetryTime > Date.now() && (
                        <div style={{ marginTop: '5px', fontSize: '12px' }}>
                            Retry in {Math.ceil((rateLimitRetryTime - Date.now()) / 1000)} seconds
                        </div>
                    )}
                </div>
            )}

            {/* Main content area */}
            <div className="main-content-area">
                {view === 'healers' ? (
                    <div className='healerFrame'>
                        {initHealers ? (
                            <Healers
                                data={{
                                    healers: filteredHealers.length > 0 ? filteredHealers : (healers || initHealers),
                                    initHealers,
                                    markers: currMarkers
                                }}
                                getHealersWithFilter={getHealersWithFilter}
                                setHealers={setHealers}
                                range={range}
                                setRange={setRange}
                                getMarkers={getMarkers}
                                selectedHealerId={selectedHealerId}
                                setSelectedHealerId={setSelectedHealerId}
                            />
                        ) : (
                            <div className="loaderContainer">
                                <div className="loader"></div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className='mapFrame'>
                        {markers && initHealers && view === 'map' && !isViewTransitioning && !isMapCleaning ? (
                            <div style={{ width: '100%', height: '100%' }}>
                                <Map
                                    key={mapKey}
                                    data={{ healers: healers || initHealers, markers: currMarkers }}
                                    handleActiveMarker={handleActiveMarker}
                                    active={active}
                                    setActive={setActive}
                                    range={range}
                                    location={location}
                                    setLocation={setLocation}
                                    onMapsLoaded={handleMapsLoaded}
                                    setSelectedHealerId={setSelectedHealerId}
                                />
                            </div>
                        ) : (
                            <div className="loaderContainer">
                                <div className="loader"></div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Search;

